import BackButton from '@/components/ui/BackButton'
import DataGridTable from '@/components/shared/DataGridTable'
import { useDataGridConfig, filterRows } from '@/hooks/useDataGridConfig'
import { mockDeduplicationData } from '@/mocks/deduplication'
import { useLinkCandidatesQuery, useMergeCandidatesQuery } from '@/services/deduplication'
import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import type { DeferredRecord, HistoryRecord } from '@/types/deduplication'
import type { GridColDef } from '@mui/x-data-grid'
import { LinkOutlined, Merge } from '@mui/icons-material'
import { Box, CircularProgress, Pagination, Paper, Tab, Tabs, Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import EmptyState from './sections/empty-state'
import RecommendationCard from './sections/recommendation-card'

const CANDIDATES_PER_PAGE = 10

const deferredColumns: GridColDef<DeferredRecord>[] = [
  { field: 'patientRecord', headerName: 'Patient record', flex: 1, minWidth: 180 },
  { field: 'numberOfRecords', headerName: 'Records', type: 'number', width: 100 },
  { field: 'suggestedAction', headerName: 'Suggested action', width: 140 },
  { field: 'deferredBy', headerName: 'Deferred by', width: 180 },
  { field: 'dateDeferred', headerName: 'Date deferred', width: 160 }
]

const historyColumns: GridColDef<HistoryRecord>[] = [
  { field: 'patientRecord', headerName: 'Patient record', flex: 1, minWidth: 180 },
  { field: 'numberOfRecords', headerName: 'Records', type: 'number', width: 100 },
  { field: 'action', headerName: 'Action', width: 120 },
  { field: 'by', headerName: 'By', width: 180 },
  { field: 'date', headerName: 'Date', width: 160 }
]

// TODO: uncomment SortOption and SortSelect when PHP API supports sort_direction param on /potential_duplicate_patients
// type SortOption = 'newest' | 'oldest'

function TabHeader({ title, description }: { title: string; description: string }) {
  return (
    <>
      <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, color: colors.brand.primary, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, mb: 3, maxWidth: 700 }}>
        {description}
      </Typography>
    </>
  )
}

// function SortSelect({ value, onChange }: { value: SortOption; onChange: (v: SortOption) => void }) {
//   return (
//     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
//       <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.medium, color: colors.brand.primary }}>
//         Sorted By:
//       </Typography>
//       <Select
//         value={value}
//         onChange={e => onChange(e.target.value as SortOption)}
//         size='small'
//         variant='standard'
//         disableUnderline
//         sx={{
//           fontSize: fontSizes.base,
//           fontWeight: fontWeights.medium,
//           color: colors.brand.primary,
//           '& .MuiSelect-select': { py: 0 }
//         }}
//       >
//         <MenuItem value='newest'>Newest First</MenuItem>
//         <MenuItem value='oldest'>Oldest First</MenuItem>
//       </Select>
//     </Box>
//   )
// }

export default function DeduplicationPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // TODO: uncomment when PHP API supports sort_direction param on /potential_duplicate_patients
  // const [mergeSortBy, setMergeSortBy] = useState<SortOption>('newest')
  // const [linkSortBy, setLinkSortBy] = useState<SortOption>('newest')

  const activeTab: number = searchParams.get('tab') === 'link' ? 1 : 0
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)
  const mergePage = activeTab === 0 ? page : 1
  const linkPage = activeTab === 1 ? page : 1
  const patientHash = searchParams.get('patientHash') ?? undefined

  const setActiveTab = useCallback(
    (next: number) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          params.set('tab', next === 1 ? 'link' : 'merge')
          params.delete('page')
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setPage = useCallback(
    (next: number) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          params.set('page', String(next))
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const {
    data: mergeResponse,
    isLoading: isMergeLoading,
    isError: isMergeError
  } = useMergeCandidatesQuery({ page: mergePage, perPage: CANDIDATES_PER_PAGE, sort: 'newest', patientHash })

  const {
    data: linkResponse,
    isLoading: isLinkLoading,
    isError: isLinkError
  } = useLinkCandidatesQuery({ page: linkPage, perPage: CANDIDATES_PER_PAGE, sort: 'newest', patientHash })

  const mergeItems = mergeResponse?.data ?? []
  const linkItems = linkResponse?.data ?? []
  const mergeCount = mergeResponse?.total ?? 0
  const linkCount = linkResponse?.total ?? 0
  const mergeTotalPages = mergeResponse?.total_pages ?? 0
  const linkTotalPages = linkResponse?.total_pages ?? 0

  const deferredConfig = useDataGridConfig({ tableId: 'deferred-requests', syncToUrl: false })
  const historyConfig = useDataGridConfig({ tableId: 'history', syncToUrl: false })
  const filteredDeferred = useMemo(
    () => filterRows(mockDeduplicationData.deferredRequests, deferredConfig.filters),
    [deferredConfig.filters]
  )
  const filteredHistory = useMemo(
    () => filterRows(mockDeduplicationData.history, historyConfig.filters),
    [historyConfig.filters]
  )

  const tabLabel = (icon: React.ReactNode, label: string, count: number) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      {label} ({count})
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <BackButton fallbackPath='/patients' defaultLabel='Back to patients' />

      <Typography variant='pageTitle' component='h1'>
        Patients To Merge Or Link
      </Typography>

      <Paper variant='outlined' sx={{ borderRadius: borderRadius.md, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as number)}
          sx={{ borderBottom: `1px solid ${colors.border.default}` }}
        >
          <Tab label={tabLabel(<Merge sx={{ fontSize: 18 }} />, 'Merge patients', mergeCount)} />
          <Tab label={tabLabel(<LinkOutlined sx={{ fontSize: 18 }} />, 'Link patients', linkCount)} />
          {/* <Tab
            label={tabLabel(<Schedule sx={{ fontSize: 18 }} />, 'Deferred requests', data.deferredRequests.length)}
          />
          <Tab label={tabLabel(<History sx={{ fontSize: 18 }} />, 'History', data.history.length)} /> */}
        </Tabs>

        <Box sx={{ p: 4 }}>
          {activeTab === 0 &&
            (isMergeLoading && mergeItems.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : isMergeError ? (
              <EmptyState
                title='Failed to load merge candidates'
                description='There was an error loading duplicate patient records. Please try again later.'
              />
            ) : mergeCount > 0 ? (
              <>
                <TabHeader
                  title="It's recommended to merge the following patients into a single patient record"
                  description='Patients should only be merged when there is strong evidence they refer to the same individual. Their first name, last name, gender, and date of birth must match closely, with any minor discrepancies attributable to human error.'
                />
                {/* TODO: uncomment when PHP API supports sort_direction param on /potential_duplicate_patients
                <SortSelect
                  value={mergeSortBy}
                  onChange={v => {
                    setMergeSortBy(v)
                    setMergePage(1)
                  }}
                /> */}
                {mergeItems.map(candidate => (
                  <RecommendationCard key={candidate.duplicate_id} type='merge' candidate={candidate} />
                ))}
                {mergeTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={mergeTotalPages}
                      page={mergePage}
                      onChange={(_, p) => setPage(p)}
                      color='primary'
                      shape='rounded'
                    />
                  </Box>
                )}
              </>
            ) : (
              <EmptyState
                title='No duplicate patients to merge'
                description='There are currently no duplicate patient records to merge'
              />
            ))}

          {activeTab === 1 &&
            (isLinkLoading && linkItems.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : isLinkError ? (
              <EmptyState
                title='Failed to load link candidates'
                description='There was an error loading patient link candidates. Please try again later.'
              />
            ) : linkCount > 0 ? (
              <>
                <TabHeader
                  title="It's recommended to link the following patients"
                  description="Link a patient together when the patient's details are match on First name, Last Name, date of birth and Gender. This link helps the prescribers have a complete view of the patient."
                />
                {/* TODO: uncomment when PHP API supports sort_direction param on /potential_duplicate_patients
                <SortSelect
                  value={linkSortBy}
                  onChange={v => {
                    setLinkSortBy(v)
                    setLinkPage(1)
                  }}
                /> */}
                {linkItems.map(candidate => (
                  <RecommendationCard key={candidate.duplicate_id} type='link' candidate={candidate} />
                ))}
                {linkTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={linkTotalPages}
                      page={linkPage}
                      onChange={(_, p) => setPage(p)}
                      color='primary'
                      shape='rounded'
                    />
                  </Box>
                )}
              </>
            ) : (
              <EmptyState title='No patients to link' description='There are currently no patient records to link' />
            ))}

          {activeTab === 2 && (
            <>
              <TabHeader
                title='View all deferred requests'
                description="These records were deferred because a team member wasn't confident to link or merge them. Another CS team member can now review them."
              />
              <DataGridTable
                {...deferredConfig}
                filterMode='client'
                tableId='deferred-requests'
                columns={deferredColumns}
                rows={filteredDeferred}
                rowCount={filteredDeferred.length}
              />
            </>
          )}

          {activeTab === 3 && (
            <>
              <TabHeader
                title='Recently Merged or linked Patients'
                description='View recently merged or linked patients by the CS team.'
              />
              <DataGridTable
                {...historyConfig}
                filterMode='client'
                tableId='history'
                columns={historyColumns}
                rows={filteredHistory}
                rowCount={filteredHistory.length}
              />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
